import { useGraviChatStore } from '@/components/GraviChat/store'
import { useMutation } from '@tanstack/react-query'
import { GraviRequestError, graviSend } from './request'
import type { SubmitTurnBody, TurnAccepted } from './types'

/**
 * Submit a turn on a thread. Returns the async `task_id` whose reply is then
 * streamed via `useGraviStream`. Ported from S&D `api/gravi/useSubmitTurn.ts`.
 *
 * On failure the error is surfaced as an inline red bubble in the chat (via the
 * store) rather than a toast, so failures don't disappear behind a dismissing
 * notification. The gravitutor `thread_busy` error gets a tailored message.
 *
 * TODO(genapi): swap to `$api.useMutation('post', '/gravitutor/threads/{thread_id}/turn')`
 * once the path is in the generated OpenAPI types.
 */
export function useSubmitTurn() {
  return useMutation({
    mutationFn: ({
      threadId,
      body,
    }: {
      threadId: string
      body: SubmitTurnBody
    }) =>
      graviSend<TurnAccepted>(
        'POST',
        `/gravitutor/threads/${threadId}/turn`,
        body,
      ),
    onError: (error) => {
      const setSubmitError = useGraviChatStore.getState().setSubmitError
      const detail =
        error instanceof GraviRequestError ? error.detail : undefined

      if (
        typeof detail === 'object' &&
        detail !== null &&
        'kind' in detail &&
        (detail as { kind?: unknown }).kind === 'thread_busy'
      ) {
        setSubmitError(
          'Another task is already running on this thread. Wait for it to finish.',
        )
        return
      }

      const text =
        typeof detail === 'string'
          ? detail
          : 'Could not send message — please try again.'
      setSubmitError(text)
    },
  })
}
